import React, { useCallback, useEffect, useState } from "react";
import { api } from "../services/api";
import {
  formatSourceKind,
  formatWhen,
  surfaceKindLabel,
} from "../utils/knowledgeSurfaceKinds";
import { userCanContribute } from "../utils/userRoles";

function apiErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    const msg = typeof first === "string" ? first : first?.msg;
    if (msg) return String(msg);
  }
  if (err?.message) return String(err.message);
  return fallback;
}

const KnowledgeSourceDetailPanel = ({
  sourceVersionId,
  anchorContext = null,
  onClose = null,
  onChanged = null,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const canManage = userCanContribute();

  const loadDetail = useCallback(async () => {
    const vid = String(sourceVersionId || "").trim();
    if (!vid) {
      setDetail(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await api.getKnowledgeSourceDetail(vid);
      setDetail(data || null);
    } catch (err) {
      setDetail(null);
      setError(err?.message || "Could not load document.");
    } finally {
      setLoading(false);
    }
  }, [sourceVersionId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleUnscope = async () => {
    if (!anchorContext?.id || !detail?.source_version_id) return;
    const scopeLabel =
      anchorContext.scopeType === "knowledge_surface"
        ? `category "${anchorContext.name || "this category"}"`
        : `application "${anchorContext.name || "this application"}"`;
    const shared = Number(detail.scope_count || 0) > 1;
    const msg = shared
      ? `Remove this document from ${scopeLabel}? It will stay indexed for other scopes unless no scopes remain.`
      : `Remove this document from ${scopeLabel}? With no other scopes, indexed passages will be removed from search.`;
    if (!window.confirm(`${msg}\n\nThis does not remove map entities from architecture extraction.`)) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.unscopeKnowledgeSource(detail.source_version_id, {
        anchor_kind: anchorContext.scopeType,
        anchor_id: anchorContext.id,
      });
      if (typeof onChanged === "function") onChanged();
      if (typeof onClose === "function") onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not remove document from scope."));
    } finally {
      setBusy(false);
    }
  };

  const handlePurge = async () => {
    if (!detail?.source_version_id) return;
    if (
      !window.confirm(
        "Delete this document from search everywhere? All indexed passages will be removed.\n\nThis does not remove entities already on your map from past extraction."
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.purgeKnowledgeSource(detail.source_version_id);
      if (typeof onChanged === "function") onChanged();
      if (typeof onClose === "function") onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not delete document from search."));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="text-sm text-slate-500">Loading document…</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="text-sm text-red-500">{error || "Document not found."}</p>
        {onClose ? (
          <button type="button" onClick={onClose} className="mt-2 text-sm text-slate-600">
            Close
          </button>
        ) : null}
      </div>
    );
  }

  const fragments = Array.isArray(detail.fragments) ? detail.fragments : [];
  const scopes = Array.isArray(detail.scopes) ? detail.scopes : [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="mb-2 text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              Back
            </button>
          ) : null}
          <h3 className="text-base font-semibold text-slate-900">{detail.title}</h3>
          <p className="text-xs text-slate-500 mt-1">
            {formatSourceKind(detail.source_kind)}
            {detail.created_at ? ` · ${formatWhen(detail.created_at)}` : ""}
            {detail.active_fragment_count != null
              ? ` · ${detail.active_fragment_count} active passages`
              : ""}
            {detail.body_fragment_count != null
              ? ` · ${detail.body_fragment_count} body`
              : ""}
            {detail.index_status
              ? ` · ${
                  detail.index_status === "indexed"
                    ? "Indexed"
                    : detail.index_status === "needs_body"
                      ? "Needs body index"
                      : detail.index_status === "needs_embed"
                        ? "Needs embeddings"
                        : detail.index_status
                }`
              : ""}
            {!detail.is_active ? " · Removed from search" : ""}
          </p>
        </div>
        {canManage && detail.is_active !== false ? (
          <div className="flex flex-wrap gap-2">
            {anchorContext?.id ? (
              <button
                type="button"
                disabled={busy}
                onClick={handleUnscope}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Remove from {anchorContext.scopeType === "knowledge_surface" ? "category" : "app"}
              </button>
            ) : null}
            <button
              type="button"
              disabled={busy}
              onClick={handlePurge}
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              Delete from search
            </button>
          </div>
        ) : null}
      </div>

      {scopes.length > 0 ? (
        <div className="text-xs text-slate-600">
          <span className="font-medium text-slate-700">Indexed under: </span>
          {scopes.map((scope, idx) => (
            <span key={`${scope.target_kind}-${scope.target_id}`}>
              {idx > 0 ? ", " : ""}
              {scope.label || scope.target_id}
            </span>
          ))}
        </div>
      ) : null}

      {detail.text ? (
        <div className="rounded-md border border-slate-100 bg-slate-50/80 p-3">
          <p className="text-xs font-medium text-slate-700 mb-2">Document text</p>
          <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">
            {detail.text}
          </pre>
          {detail.text_truncated ? (
            <p className="text-[11px] text-slate-500 mt-2">Showing an excerpt of the full document.</p>
          ) : null}
        </div>
      ) : null}

      {fragments.length > 0 ? (
        <div>
          <p className="text-xs font-medium text-slate-700 mb-2">Indexed passages</p>
          <ul className="space-y-2 max-h-72 overflow-y-auto">
            {fragments.map((frag) => (
              <li
                key={frag.fragment_id}
                className={`rounded border px-2 py-1.5 text-xs ${
                  frag.is_active
                    ? "border-slate-100 bg-white text-slate-700"
                    : "border-slate-100 bg-slate-50 text-slate-400 line-through"
                }`}
              >
                <span className="text-[10px] text-slate-400 mr-2">#{frag.chunk_index + 1}</span>
                {frag.preview}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {error ? <p className="text-sm text-red-500">{error}</p> : null}
    </div>
  );
};

export default KnowledgeSourceDetailPanel;
